import React, { useEffect, useMemo, useState } from "react";

import { CSpinner } from "@coreui/react";
import {
  RiFireFill,
  RiFlashlightFill,
  RiLockFill,
  RiMedal2Fill
} from "react-icons/ri";

import URL from "../../../config/api";
import axios from "../../../config/axios";

/**
 * As medalhas do aluno, AGRUPADAS POR FAMÍLIA.
 *
 * O QUE ESTAVA ERRADO, e é o que esta versão conserta: o endpoint devolve doze
 * medalhas de DUAS famílias — seis de `streak` (dias seguidos de ofensiva) e seis de
 * `xp_total` (XP acumulado) —, e o campo `type` que as distingue vinha na resposta e
 * era IGNORADO. O resultado eram doze cartões idênticos em fila, todos com o mesmo
 * troféu amarelo, misturando "5 dias seguidos" com "1500 XP" como se fossem a mesma
 * espécie de coisa. Nada na tela dizia por que uma medalha vem antes da outra, embora
 * o backend já as ordene por `type` e depois por `threshold`.
 *
 * Agora cada família é uma fileira com nome, e cada uma tem o PRÓPRIO SÍMBOLO — chama
 * para ofensiva, raio para XP. O grau dentro da família não precisa de símbolo
 * distinto: ele está no texto ("5 dias", "130 dias") e a ordem já é crescente.
 *
 * A MEDALHA BLOQUEADA MOSTRA O SÍMBOLO DA FAMÍLIA, em cinza, com um cadeado no canto.
 * O antigo `.achievement-locked` aplicava `opacity: .45` e `grayscale(1)` no cartão
 * INTEIRO, o que apagava o texto junto — e o texto de uma medalha bloqueada é
 * justamente o que ela tem de mais útil, porque diz o que fazer para ganhá-la. É a
 * mesma decisão da silhueta na coleção de cartas: mostre o que falta, não um vazio.
 *
 * @param {string}   token
 * @param {Function} [onResumo] avisa o cabeçalho da seção: { conquistadas, total }
 */

/* As FAMÍLIAS conhecidas. `type` é uma string de 20 caracteres no banco
   (`gam_achievements.type`), então uma família nova pode aparecer sem este arquivo
   saber — daí o fallback, que é o que impede a medalha nova de ficar invisível. O
   nome do grupo é o rótulo que o aluno lê; o `type` cru nunca aparece na tela. */
const FAMILIAS = {
  streak: {
    nome: "Ofensiva",
    resumo: "Dias seguidos estudando",
    Icone: RiFireFill,
    tom: "fogo"
  },
  xp_total: {
    nome: "Experiência",
    resumo: "XP acumulado ao longo do ano",
    Icone: RiFlashlightFill,
    tom: "xp"
  }
};

const FAMILIA_PADRAO = {
  nome: "Outras conquistas",
  resumo: "",
  Icone: RiMedal2Fill,
  tom: "neutro"
};

function familiaDe(type) {
  return (type && FAMILIAS[type]) || FAMILIA_PADRAO;
}

const formatarData = isoDate => {
  if (!isoDate) return "";
  const [ano, mes, dia] = isoDate.split("-");
  return `${dia}/${mes}/${ano}`;
};

/**
 * Agrupa as medalhas por `type`, PRESERVANDO A ORDEM em que vieram.
 *
 * O backend já ordena por `type` e depois por `threshold`, então a ordem da resposta é
 * a ordem que faz sentido na tela: a família na sequência em que o servidor a
 * apresentou, e dentro dela do grau mais baixo para o mais alto. Reordenar aqui
 * (alfabético, por exemplo) jogaria fora esse trabalho e poria "Ano Perfeito" antes de
 * "Primeira Semana".
 */
export function agruparPorFamilia(medalhas = []) {
  const grupos = [];
  const porType = {};

  medalhas.forEach(medalha => {
    if (!medalha) return;
    const type = medalha.type || "outras";

    if (!porType[type]) {
      porType[type] = { type, familia: familiaDe(medalha.type), medalhas: [] };
      grupos.push(porType[type]);
    }
    porType[type].medalhas.push(medalha);
  });

  return grupos;
}

function Medalhas({ token, onResumo }) {
  const [medalhas, setMedalhas] = useState(null);
  const [falhou, setFalhou] = useState(false);

  useEffect(() => {
    if (!token) return undefined;

    let cancelado = false;

    axios
      .get(`${URL.api}achievements/me`, {
        headers: { authorization: `Bearer ${token}` }
      })
      .then(resp => {
        if (cancelado) return;

        // 204 é a resposta para quem NÃO é aluno (ver `AchievementController@me`), e
        // vem sem corpo: `resp.data` é vazio, então sem esta guarda o acesso a
        // `.success` já bastaria para o professor ver a seção como falha.
        if (resp.status === 204) {
          setMedalhas([]);
          return;
        }

        if (resp.status === 200 && resp.data && resp.data.success) {
          setMedalhas(resp.data.data || []);
          return;
        }

        // 200 com `success: false` é como este backend devolve erro de aplicação, e
        // antes disto caía num catch vazio: a página ficava em spinner eterno sem
        // nada no console.
        // eslint-disable-next-line no-console
        console.warn(
          "Medalhas: o servidor respondeu sem sucesso.",
          resp.data && resp.data.error
        );
        setFalhou(true);
      })
      .catch(erro => {
        if (cancelado) return;

        const status = erro && erro.response && erro.response.status;
        // eslint-disable-next-line no-console
        console.warn(
          `Medalhas: não foi possível buscar do servidor${
            status ? ` (HTTP ${status})` : ""
          }.`
        );
        setFalhou(true);
      });

    return () => {
      cancelado = true;
    };
  }, [token]);

  const grupos = useMemo(() => agruparPorFamilia(medalhas || []), [medalhas]);

  /* O RESUMO SOBE PARA O CABEÇALHO da seção, que é quem mostra "3 de 12". Num efeito
     e não no corpo da renderização: chamar o callback do pai enquanto se renderiza é
     `setState` durante render, que o React 18 reclama e que pode entrar em laço. */
  useEffect(() => {
    if (!onResumo || medalhas === null) return;

    onResumo({
      conquistadas: medalhas.filter(medalha => medalha && medalha.unlocked)
        .length,
      total: medalhas.length
    });
  }, [medalhas, onResumo]);

  if (falhou) {
    // Recado curto e honesto, em vez do spinner que nunca parava. O aluno não pode
    // consertar isto, mas pode saber que não é a coleção dele que está vazia.
    return (
      <p className="conquistas__aviso">
        Não foi possível carregar suas medalhas agora. Tente novamente mais
        tarde.
      </p>
    );
  }

  if (medalhas === null) {
    return (
      <div className="conquistas__carregando">
        <CSpinner color="secondary" size="sm" />
      </div>
    );
  }

  if (medalhas.length === 0) {
    return (
      <p className="conquistas__aviso">
        Nenhuma medalha disponível no momento.
      </p>
    );
  }

  return (
    <div className="conquistas__familias">
      {grupos.map(grupo => (
        <div className="conquistas__familia" key={grupo.type}>
          <div className="conquistas__familia-cabecalho">
            <h6 className="conquistas__familia-nome">{grupo.familia.nome}</h6>
            {grupo.familia.resumo && (
              <span className="conquistas__familia-resumo">
                {grupo.familia.resumo}
              </span>
            )}
          </div>

          <ul className="conquistas__grade">
            {grupo.medalhas.map(medalha => (
              <li className="conquistas__medalha-item" key={medalha.key}>
                <Medalha medalha={medalha} familia={grupo.familia} />
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

/**
 * Um cartão de medalha: medalhão, nome, o que ela pede, e a data quando conquistada.
 *
 * NÃO É CLICÁVEL, e não é esquecimento: não há nada para abrir. O cartão já mostra
 * tudo o que se sabe da medalha — não existe uma "medalha de perto" com mais
 * conteúdo, como existe para a carta.
 */
function Medalha({ medalha, familia }) {
  const { Icone } = familia;
  const conquistada = Boolean(medalha.unlocked);

  return (
    <article
      className={`conquistas__medalha conquistas__medalha--${familia.tom}${
        conquistada ? "" : " conquistas__medalha--bloqueada"
      }`}
    >
      {/* O MEDALHÃO. Mostra o símbolo da família mesmo bloqueado — o aluno precisa
          saber o que é a medalha ANTES de ganhá-la, senão a fileira de bloqueadas não
          informa nada. O cadeado diz o estado; o cinza reforça. */}
      <div className="conquistas__medalhao" aria-hidden="true">
        <Icone size={26} />
        {!conquistada && (
          <span className="conquistas__medalhao-cadeado">
            <RiLockFill size={12} />
          </span>
        )}
      </div>

      <h6 className="conquistas__medalha-nome">{medalha.name}</h6>

      {medalha.description && (
        <p className="conquistas__medalha-descricao">{medalha.description}</p>
      )}

      {/* O RODAPÉ TEM ALTURA RESERVADA (no CSS) em vez de aparecer só quando há data:
          numa fileira em que umas estão conquistadas e outras não, o cartão sem rodapé
          fica mais baixo e a fileira serrilha. */}
      <p className="conquistas__medalha-estado">
        {conquistada ? (
          <span className="conquistas__medalha-data">
            Conquistada em {formatarData(medalha.unlocked_at)}
          </span>
        ) : (
          <span className="conquistas__medalha-pendente">
            Ainda não conquistada
          </span>
        )}
      </p>
    </article>
  );
}

export default Medalhas;
